import { stringify } from "querystring";
import { ChipGroup } from "@newsela/angelou";
import { ReactNode, useEffect, useRef } from "react";

import { useRouter } from "next/router";
import { AIChip } from "@/components/AIComponents/AIChip";
import AiLunaTagFullIcon from "@/icons/AILunaTagFull/AILunaTagFullIcon";
import AIBetaTag from "@/components/AIComponents/AIBetaTag/AIBetaTag";
import AIButton from "@/components/AIComponents/AiButton";
import AIToggle from "@/components/AIComponents/AIToggle";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";
import LunaPeekingSideIcon from "@/icons/LunaPeekingSideIcon/LunaPeekingSideIcon";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import LunaSearchingIcon from "@/icons/LunaSearchingIcon/LunaSearchingIcon";
import LunaConfusedIcon from "@/icons/LunaConfusedIcon/LunaConfusedIcon";
import LunaThinkingIcon from "@/icons/LunaThinkingIcon/LunaThinkingIcon";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useSearchWithLunaContext } from "@/search/Langston/SearchWithLunaProvider";
import VisibilityPermissions from "../VisibilityPermissions/VisibilityPermissions";
import { LUNA_FILTER_PANEL_TEST_IDS } from "./LunaFilterPanel.constants";
import { LunaFilterPanelProps } from "./LunaFilterPanel.types";
import {
  panelRootCss,
  panelCardCss,
  badgesRowCss,
  sectionTitleCss,
  sectionDescriptionCss,
  subsectionTitleCss,
  chipsGroupCss,
  chipItemWrapperCss,
  primaryActionButtonCss,
  toggleWrapperCss,
  promptSectionCss,
  illustrationAffixTopRightCss,
  illustrationAffixBottomRightCss,
  textAreaOverridesCss,
  emptyStateContainerCss,
  loadingBoxCss,
  refinementLoadingBoxCss,
  visibilityPermissionCss,
  lunaFullTagCss,
  labelPromptCss,
  textareaContainerCss,
} from "./LunaFilterPanel.styles";
import { formatSelectedFilters, LunaChip } from "./LunaFilterPanel.utils";

export const LunaFilterPanel = ({
  className,
  testId,
  isAISearchEnabled,
  onToggle,
}: LunaFilterPanelProps) => {
  const { push, query, pathname } = useRouter();
  const {
    isInvalidLunaPrompt,
    isLunaError,
    isTrafficCopFetching,
    selectedLunaChips,
    setSelectedLunaChips,
    setTrafficCopPrompt,
    trafficCopPrompt,
    trafficCopResponse,
  } = useSearchWithLunaContext();

  const { handleRemoveFilter } = useFilterControls();

  const filteredValues = formatSelectedFilters(selectedLunaChips || []);

  const refinementTextareaRef = useRef<HTMLTextAreaElement>(null);

  const hasPrompt = Boolean(
    trafficCopPrompt && trafficCopPrompt.trim().length > 0,
  );

  const handleChipEvent = (_: unknown, value: string) => {
    handleRemoveFilter("", value);
    setSelectedLunaChips(
      selectedLunaChips.filter((chip: LunaChip) => {
        const chipValueFormatted = `${chip.type},${chip.value}`;

        return value !== chipValueFormatted;
      }),
    );
  };

  const onSubmitRefinement = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    setTrafficCopPrompt(refinementTextareaRef.current?.value || "");
  };

  const onRefinementKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmitRefinement();
    }
  };

  const handleSearchWithLunaToggle = () => {
    const newValue = !isAISearchEnabled;
    if (pathname === "/search") {
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.delete(LangstonParameterSchema.prompt.key);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    onToggle(newValue);
  };

  const isPostRefinement =
    !isTrafficCopFetching && !trafficCopResponse?.response;
  const refinementQuestion =
    !isTrafficCopFetching && trafficCopResponse?.response;

  useEffect(() => {
    if (refinementQuestion) {
      refinementTextareaRef.current?.focus();
    }
  }, [refinementQuestion]);

  const renderBadges = (): ReactNode => (
    <div css={badgesRowCss}>
      <div css={lunaFullTagCss}>
        <AiLunaTagFullIcon />
      </div>
      <AIBetaTag />
    </div>
  );

  const renderErrorState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h5 css={sectionTitleCss}>Something went wrong.</h5>
      <p css={sectionDescriptionCss}>
        Luna couldn't complete your request right now. Please try again in a few
        moments.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaConfusedIcon aria-hidden />
      </div>
    </div>
  );

  const renderResults = (): ReactNode => (
    <div>
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h5 css={sectionTitleCss}>25 Most relevant results for:</h5>
      <p css={sectionDescriptionCss}>"{trafficCopPrompt}"</p>

      <h5 css={subsectionTitleCss}>Filters applied by Luna:</h5>

      <ChipGroup
        groupName={LUNA_FILTER_PANEL_TEST_IDS.CHIP_GROUP}
        chipType={ChipGroup.chipTypes.input}
        onChipEvent={handleChipEvent}
        size={ChipGroup.chipSizes.medium}
        __cssFor={chipsGroupCss}
      >
        {filteredValues.map((item: LunaChip) => (
          <div key={item.value} css={chipItemWrapperCss}>
            <AIChip
              active
              text={item.label}
              value={`${item.type},${item.value}`}
              dataProps={{
                "data-qa-selector": `${LUNA_FILTER_PANEL_TEST_IDS.CHIP_PREFIX}${item.type}-${item.value}`,
                "data-chip-index": String(item.value),
              }}
            />
          </div>
        ))}
      </ChipGroup>

      {isPostRefinement ? null : renderPromptRefinement()}
    </div>
  );

  const renderPromptRefinement = (): ReactNode => (
    <form onSubmit={onSubmitRefinement} css={promptSectionCss}>
      {isTrafficCopFetching ? (
        <div
          css={refinementLoadingBoxCss}
          role="status"
          aria-live="polite"
          aria-busy
          aria-label="Loading refinement"
          data-testid="luna-refinement-loading"
        >
          <AILoadingSpinner
            dataProps={{ "data-testid": "luna-refinement-spinner" }}
          />
        </div>
      ) : (
        <>
          <div css={illustrationAffixTopRightCss}>
            <LunaPeekingSideIcon aria-hidden />
          </div>

          <h5 css={subsectionTitleCss}>Question from Luna:</h5>
          <label htmlFor="luna-refinement-textarea" css={labelPromptCss}>
            {refinementQuestion}
          </label>
          <div css={textareaContainerCss}>
            <textarea
              ref={refinementTextareaRef}
              css={textAreaOverridesCss}
              id="luna-refinement-textarea"
              placeholder="e.g., “Culture” or “History”"
              onKeyDown={onRefinementKeyDown}
              rows={3}
            />
          </div>
        </>
      )}
      {!isTrafficCopFetching && (
        <AIButton __cssFor={{ root: primaryActionButtonCss }} type="submit">
          Refine search
        </AIButton>
      )}
    </form>
  );

  const renderEmptyState = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h5 css={sectionTitleCss}>Start a new search with Luna.</h5>
      <p css={sectionDescriptionCss}>
        Tell Luna what you need to find. You can improve your search by adding
        topics, grade levels, or types of content to your prompt.
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaThinkingIcon aria-hidden />
      </div>
    </div>
  );

  const renderPromptNeedsRevision = (): ReactNode => (
    <div css={emptyStateContainerCss} aria-live="polite">
      <VisibilityPermissions containerCssFn={visibilityPermissionCss} />
      <h5 css={sectionTitleCss}>Please revise your prompt.</h5>
      <p css={sectionDescriptionCss}>
        Luna couldn’t understand your request. To find the best content, use
        specific keywords or clear phrases describing the subject with the grade
        or topics you want.{" "}
      </p>
      <div css={illustrationAffixBottomRightCss}>
        <LunaSearchingIcon aria-hidden />
      </div>
    </div>
  );

  const renderContent = (): ReactNode => {
    if (isLunaError) return renderErrorState();
    if (isInvalidLunaPrompt) return renderPromptNeedsRevision();
    if (!hasPrompt) return renderEmptyState();
    return renderResults();
  };

  return (
    <div
      css={panelRootCss}
      className={className}
      data-testid={testId || LUNA_FILTER_PANEL_TEST_IDS.CONTAINER}
    >
      <div css={toggleWrapperCss}>
        <AIToggle
          pressed={isAISearchEnabled}
          onToggle={handleSearchWithLunaToggle}
          ariaLabel="Search with Luna"
          labelText="Search with Luna"
          qaSelector="search-with-luna-toggle"
          description="Toggle Search with Luna"
        />
      </div>

      <div css={panelCardCss(isTrafficCopFetching)}>
        {isTrafficCopFetching ? (
          <div css={loadingBoxCss} aria-live="polite">
            <AILoadingSpinner
              dataProps={{ "data-testid": "luna-loading-suggestions-spinner" }}
            />
          </div>
        ) : (
          <>
            {renderBadges()}
            {renderContent()}
          </>
        )}
      </div>
    </div>
  );
};

LunaFilterPanel.displayName = "LunaFilterPanel";
